import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';

import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { ErdDetailPanel } from './components/ErdDetailPanel';
import { ErdGraph } from './components/ErdGraph';
import { ErdOverlayControls } from './components/ErdOverlayControls';
import { ErdReviewPanel } from './components/ErdReviewPanel';
import { useErdData } from './hooks/useErdData';
import { useErdLayout } from './hooks/useErdLayout';
import { useHeuristicInference } from './hooks/useHeuristicInference';
import { useOverlayState } from './hooks/useOverlayState';
import { ErdRelationship } from './types';

interface ErdViewProps {
  modelUniqueId: string;
}

export const ErdView: FC<ErdViewProps> = ({ modelUniqueId }) => {
  const navigate = useNavigate();
  const projectScope = useUrlProjectParams();
  const linkToResourceDetails = useLinkToExplorer('resourceDetails');
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateHeight = () => {
      const rect = el.getBoundingClientRect();
      const available = window.innerHeight - rect.top;
      setContainerHeight(Math.max(400, available));
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  const {
    models,
    modelMap,
    relationshipTestEdges,
    semanticLayerEdges,
    fetching,
    error,
  } = useErdData();

  const { overlays, toggleOverlay } = useOverlayState();

  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [reviewingRelationship, setReviewingRelationship] =
    useState<ErdRelationship | null>(null);
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Filter edges to only those that directly reference the current model
  const directRelTestEdges = useMemo(
    () =>
      relationshipTestEdges.filter(
        (e) => e.sourceModelId === modelUniqueId || e.targetModelId === modelUniqueId,
      ),
    [relationshipTestEdges, modelUniqueId],
  );

  const directSemanticEdges = useMemo(
    () =>
      semanticLayerEdges.filter(
        (e) => e.sourceModelId === modelUniqueId || e.targetModelId === modelUniqueId,
      ),
    [semanticLayerEdges, modelUniqueId],
  );

  // Get the set of directly related model IDs (neighbors + self)
  const directModelIds = useMemo(() => {
    const ids = new Set<string>([modelUniqueId]);
    for (const e of directRelTestEdges) {
      ids.add(e.sourceModelId);
      ids.add(e.targetModelId);
    }
    for (const e of directSemanticEdges) {
      ids.add(e.sourceModelId);
      ids.add(e.targetModelId);
    }
    return ids;
  }, [modelUniqueId, directRelTestEdges, directSemanticEdges]);

  // Filter models to only include the current model and its direct neighbors
  const directModels = useMemo(
    () => models.filter((m) => directModelIds.has(m.uniqueId)),
    [models, directModelIds],
  );

  const allExplicitEdges = useMemo(
    () => [...directRelTestEdges, ...directSemanticEdges],
    [directRelTestEdges, directSemanticEdges],
  );

  const heuristicEdges = useHeuristicInference(directModels, allExplicitEdges);

  // Filter heuristic edges to only those involving the focused model
  const directHeuristicEdges = useMemo(
    () =>
      heuristicEdges.filter(
        (e) => e.sourceModelId === modelUniqueId || e.targetModelId === modelUniqueId,
      ),
    [heuristicEdges, modelUniqueId],
  );

  // Expand directModels to also include neighbors discovered via heuristic edges
  const expandedModelIds = useMemo(() => {
    const ids = new Set(directModelIds);
    for (const e of directHeuristicEdges) {
      ids.add(e.sourceModelId);
      ids.add(e.targetModelId);
    }
    return ids;
  }, [directModelIds, directHeuristicEdges]);

  const filteredModels = useMemo(
    () => models.filter((m) => expandedModelIds.has(m.uniqueId)),
    [models, expandedModelIds],
  );

  const explicitEdges = useMemo(() => {
    const edges: ErdRelationship[] = [];
    if (overlays.relationshipTests) {
      edges.push(...directRelTestEdges);
    }
    if (overlays.semanticLayer) {
      edges.push(...directSemanticEdges);
    }
    return edges;
  }, [overlays, directRelTestEdges, directSemanticEdges]);

  const visibleHeuristicEdges = useMemo(() => {
    if (!overlays.heuristicInferred) return [];
    return directHeuristicEdges
      .filter((e) => !dismissedIds.has(e.id))
      .map((e) => ({
        ...e,
        approvalStatus: approvedIds.has(e.id)
          ? ('approved' as const)
          : e.approvalStatus,
      }));
  }, [overlays.heuristicInferred, directHeuristicEdges, approvedIds, dismissedIds]);

  const allVisibleEdges = useMemo(
    () => [...explicitEdges, ...visibleHeuristicEdges],
    [explicitEdges, visibleHeuristicEdges],
  );

  const pendingInferredCount = useMemo(
    () =>
      visibleHeuristicEdges.filter(
        (e) => e.approvalStatus === 'pending' && e.confidence === 'high',
      ).length,
    [visibleHeuristicEdges],
  );

  const handleSelectModel = useCallback((modelId: string) => {
    setSelectedModelId((prev) => (prev === modelId ? null : modelId));
    setReviewingRelationship(null);
  }, []);

  const handleEdgeClick = useCallback((relationship: ErdRelationship) => {
    if (relationship.edgeSource === 'heuristicInferred') {
      setReviewingRelationship(relationship);
      setSelectedModelId(null);
    }
  }, []);

  const handlePaneClick = useCallback(() => {
    setSelectedModelId(null);
    setReviewingRelationship(null);
  }, []);

  const handleApprove = useCallback((relationshipId: string) => {
    setApprovedIds((prev) => new Set(prev).add(relationshipId));
  }, []);

  const handleDismiss = useCallback((relationshipId: string) => {
    setDismissedIds((prev) => new Set(prev).add(relationshipId));
    setReviewingRelationship(null);
  }, []);

  const handleSetFocus = useCallback(
    (targetModelId: string) => {
      if (!projectScope) return;
      const url = linkToResourceDetails({
        ...projectScope,
        dbtUniqueId: targetModelId,
        tab: 'relationships',
      });
      navigate(url);
    },
    [projectScope, linkToResourceDetails, navigate],
  );

  const handleApproveAllHighConfidence = useCallback(() => {
    setApprovedIds((prev) => {
      const next = new Set(prev);
      for (const e of visibleHeuristicEdges) {
        if (e.confidence === 'high' && e.approvalStatus === 'pending') {
          next.add(e.id);
        }
      }
      return next;
    });
  }, [visibleHeuristicEdges]);

  const { nodes, edges } = useErdLayout({
    models: filteredModels,
    relationships: allVisibleEdges,
    selectedModelId,
    focusedModelId: modelUniqueId,
    highlightedModelId: null,
    onSelectModel: handleSelectModel,
    onEdgeClick: handleEdgeClick,
  });

  if (fetching) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <div className="text-sm text-fgDecorative">Loading relationship data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <div className="text-sm text-fgDanger">
          Error loading relationship data: {error.message}
        </div>
      </div>
    );
  }

  if (filteredModels.length <= 1 && allVisibleEdges.length === 0) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <div className="text-sm text-fgDecorative">
          No relationships found for this model.
        </div>
      </div>
    );
  }

  const selectedModel = selectedModelId ? modelMap.get(selectedModelId) : null;
  const reviewSourceName = reviewingRelationship
    ? (modelMap.get(reviewingRelationship.sourceModelId)?.name ??
      reviewingRelationship.sourceModelId.split('.').pop() ??
      '')
    : '';
  const reviewTargetName = reviewingRelationship
    ? (modelMap.get(reviewingRelationship.targetModelId)?.name ??
      reviewingRelationship.targetModelId.split('.').pop() ??
      '')
    : '';

  return (
    <div
      ref={containerRef}
      className="flex w-full"
      style={{ height: containerHeight ? `${containerHeight}px` : '400px' }}
      data-testid="erd-view"
    >
      {/* Left panel: overlay controls */}
      <div className="flex w-48 shrink-0 flex-col gap-4 border-r border-borderMuted bg-bgMain p-3">
        <ErdOverlayControls
          overlays={overlays}
          onToggle={toggleOverlay}
          pendingInferredCount={pendingInferredCount}
          onApproveAllHighConfidence={handleApproveAllHighConfidence}
        />

        <div className="mt-auto text-[10px] text-fgDecorative">
          {filteredModels.length} models &middot; {allVisibleEdges.length} relationships
        </div>
      </div>

      {/* Main graph area */}
      <div className="relative flex-1">
        <ReactFlowProvider>
          <ErdGraph nodes={nodes} edges={edges} onPaneClick={handlePaneClick} />
        </ReactFlowProvider>
      </div>

      {/* Right panel: model detail or relationship review */}
      {selectedModel && !reviewingRelationship && (
        <ErdDetailPanel
          model={selectedModel}
          relationships={allVisibleEdges}
          onClose={() => setSelectedModelId(null)}
          onSetFocus={handleSetFocus}
        />
      )}

      {reviewingRelationship && (
        <ErdReviewPanel
          relationship={
            approvedIds.has(reviewingRelationship.id)
              ? { ...reviewingRelationship, approvalStatus: 'approved' }
              : reviewingRelationship
          }
          sourceModelName={reviewSourceName}
          targetModelName={reviewTargetName}
          onApprove={handleApprove}
          onDismiss={handleDismiss}
          onClose={() => setReviewingRelationship(null)}
        />
      )}
    </div>
  );
};
